import { useRef, useEffect, useMemo } from 'react'
import { useAgentChat } from '../hooks/useAgentChat'
import { PromptInput } from './PromptInput'
import type { ChatMessage } from '../types'

interface AgentChatProps {
  agentId: string
  projectId: string
  isPublic?: boolean
  onFollowUp?: (msg: string) => void
  onAgentStatusChange?: (running: boolean) => void
  onStatsChange?: (stats: BuildStats) => void
}

export interface BuildStats {
  filesCreated: number
  linesWritten: number
  testsRun: number
  phase: 'planning' | 'coding' | 'testing' | 'complete'
}

type ActivityType = 'plan' | 'file' | 'code' | 'test' | 'install' | 'command' | 'review' | 'success' | 'error' | 'info'

function classifyMessage(content: string): ActivityType {
  const lower = content.toLowerCase()
  if (lower.includes('creating') || lower.includes('wrote') || lower.includes('generated') || lower.includes('new file')) return 'file'
  if (lower.includes('installing') || lower.includes('dependencies') || lower.includes('npm install') || lower.includes('go mod') || lower.includes('pip install')) return 'install'
  if (lower.includes('test') || lower.includes('pass') || lower.includes('fail') || lower.includes('spec')) return 'test'
  if (lower.includes('review') || lower.includes('checking') || lower.includes('lint')) return 'review'
  if (lower.includes('running') || lower.includes('executing') || lower.includes('building') || lower.includes('compiling')) return 'command'
  if (lower.includes('plan') || lower.includes('analyzing') || lower.includes('breaking down') || lower.includes('decompos')) return 'plan'
  if (lower.includes('complete') || lower.includes('success') || lower.includes('done') || lower.includes('finished') || lower.includes('ready')) return 'success'
  if (lower.includes('error') || lower.includes('failed')) return 'error'
  return 'code'
}

function getPhaseFromMessages(messages: ChatMessage[]): BuildStats['phase'] {
  if (messages.length === 0) return 'planning'
  const last5 = messages.slice(-5)
  const combined = last5.map(m => m.content.toLowerCase()).join(' ')
  if (combined.includes('test') || combined.includes('pass') || combined.includes('spec')) return 'testing'
  if (combined.includes('complete') || combined.includes('success') || combined.includes('finished') || combined.includes('done')) return 'complete'
  if (combined.includes('creat') || combined.includes('writ') || combined.includes('generat') || combined.includes('implement')) return 'coding'
  if (messages.length < 3) return 'planning'
  return 'coding'
}

const activityIcons: Record<ActivityType, { icon: JSX.Element; color: string; bg: string }> = {
  plan: {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />,
    color: 'text-violet-500',
    bg: 'bg-violet-500/10',
  },
  file: {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
  },
  code: {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />,
    color: 'text-cyan-500',
    bg: 'bg-cyan-500/10',
  },
  test: {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
  },
  install: {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
  },
  command: {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />,
    color: 'text-orange-500',
    bg: 'bg-orange-500/10',
  },
  review: {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />,
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
  },
  success: {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />,
    color: 'text-green-500',
    bg: 'bg-green-500/10',
  },
  error: {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />,
    color: 'text-red-500',
    bg: 'bg-red-500/10',
  },
  info: {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
    color: 'text-gray-400',
    bg: 'bg-gray-500/10',
  },
}

function ActivityItem({ message, isLast }: { message: ChatMessage; isLast: boolean }) {
  if (message.type === 'error') {
    const style = activityIcons.error
    return (
      <div className="flex gap-3 animate-slideUp">
        <div className="flex flex-col items-center">
          <div className={`w-7 h-7 rounded-full ${style.bg} flex items-center justify-center flex-shrink-0`}>
            <svg className={`w-3.5 h-3.5 ${style.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {style.icon}
            </svg>
          </div>
          {!isLast && <div className="w-px flex-1 bg-red-200 dark:bg-red-800/30 mt-1" />}
        </div>
        <div className="flex-1 pb-4">
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 px-3 py-2 rounded-lg">
            {message.content}
          </div>
        </div>
      </div>
    )
  }

  if (message.role === 'user') {
    return (
      <div className="flex gap-3 animate-slideUp">
        <div className="flex flex-col items-center">
          <div className="w-7 h-7 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-3.5 h-3.5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          {!isLast && <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700 mt-1" />}
        </div>
        <div className="flex-1 pb-4">
          <div className="inline-block max-w-[90%] px-3 py-2 rounded-lg bg-primary-600 text-white text-sm">
            {message.content}
          </div>
        </div>
      </div>
    )
  }

  // Assistant messages — classify by content
  const activityType = message.type === 'status' ? 'info' : classifyMessage(message.content)
  const style = activityIcons[activityType]

  return (
    <div className="flex gap-3 animate-slideUp">
      <div className="flex flex-col items-center">
        <div className={`w-7 h-7 rounded-full ${style.bg} flex items-center justify-center flex-shrink-0`}>
          <svg className={`w-3.5 h-3.5 ${style.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {style.icon}
          </svg>
        </div>
        {!isLast && <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700 mt-1" />}
      </div>
      <div className="flex-1 pb-4">
        <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
          {message.content}
        </p>
      </div>
    </div>
  )
}

function WorkingIndicator() {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="w-7 h-7 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
          <div className="flex gap-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-bounce-dot" />
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-bounce-dot" style={{ animationDelay: '0.16s' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-bounce-dot" style={{ animationDelay: '0.32s' }} />
          </div>
        </div>
      </div>
      <div className="flex-1 pb-4">
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">Working...</p>
      </div>
    </div>
  )
}

export function AgentChat({ agentId, projectId, isPublic, onFollowUp, onAgentStatusChange, onStatsChange }: AgentChatProps) {
  const { messages, isAgentRunning, sendFollowUp } = useAgentChat({ agentId, projectId })
  const bottomRef = useRef<HTMLDivElement>(null)
  const statusCallbackRef = useRef(onAgentStatusChange)
  statusCallbackRef.current = onAgentStatusChange
  const statsCallbackRef = useRef(onStatsChange)
  statsCallbackRef.current = onStatsChange

  const prevRunningRef = useRef(isAgentRunning)
  useEffect(() => {
    if (prevRunningRef.current !== isAgentRunning) {
      prevRunningRef.current = isAgentRunning
      statusCallbackRef.current?.(isAgentRunning)
    }
  }, [isAgentRunning])

  // Compute build stats from messages
  const stats = useMemo<BuildStats>(() => {
    let filesCreated = 0
    let linesWritten = 0
    let testsRun = 0
    for (const msg of messages) {
      const lower = msg.content.toLowerCase()
      if (lower.includes('creat') || lower.includes('wrote') || lower.includes('generated') || lower.includes('new file')) filesCreated++
      if (lower.includes('lines') || lower.includes('wrote')) {
        const match = msg.content.match(/(\d+)\s*lines?/i)
        if (match) linesWritten += parseInt(match[1])
        else linesWritten += 20 // estimate
      }
      if (lower.includes('test') && (lower.includes('pass') || lower.includes('ran') || lower.includes('run'))) {
        const match = msg.content.match(/(\d+)\s*(test|spec|passed)/i)
        if (match) testsRun += parseInt(match[1])
        else testsRun++
      }
    }
    const phase = !isAgentRunning && messages.length > 0 ? 'complete' : getPhaseFromMessages(messages)
    return { filesCreated, linesWritten, testsRun, phase }
  }, [messages, isAgentRunning])

  useEffect(() => {
    statsCallbackRef.current?.(stats)
  }, [stats])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleFollowUp = async (msg: string) => {
    await sendFollowUp(msg)
    onFollowUp?.(msg)
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4">
        {messages.length === 0 && isAgentRunning && (
          <div className="flex flex-col items-center justify-center h-full gap-4 animate-fadeIn">
            {/* Animated spinner */}
            <div className="relative">
              <div className="w-12 h-12 rounded-full border-2 border-primary-200 dark:border-primary-800" />
              <div className="absolute inset-0 w-12 h-12 rounded-full border-2 border-transparent border-t-primary-500 animate-spin" />
              <div className="absolute inset-2 w-8 h-8 rounded-full border-2 border-transparent border-b-primary-400 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                {isPublic ? 'Spinning up your AI team...' : 'Waiting for agent output...'}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">This usually takes a few seconds</p>
            </div>
          </div>
        )}

        {/* Activity timeline */}
        {messages.length > 0 && (
          <div className="space-y-0">
            {messages.map((msg, i) => (
              <ActivityItem
                key={msg.id}
                message={msg}
                isLast={i === messages.length - 1 && !isAgentRunning}
              />
            ))}
            {isAgentRunning && <WorkingIndicator />}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {isAgentRunning && (
        <div className="border-t dark:border-gray-700 px-4 py-3">
          <PromptInput
            onSubmit={handleFollowUp}
            placeholder="Send a follow-up message..."
            loading={false}
          />
        </div>
      )}

      {/* Completion: public */}
      {!isAgentRunning && messages.length > 0 && isPublic && (
        <div className="border-t border-green-200 dark:border-green-800/30 px-4 py-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 animate-slideUp">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0 animate-scaleIn shadow-lg shadow-green-500/20">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="absolute -inset-1 rounded-full bg-green-400/20 animate-ping" style={{ animationDuration: '2s' }} />
            </div>
            <div>
              <p className="text-sm font-semibold text-green-800 dark:text-green-300">Build complete!</p>
              <p className="text-xs text-green-600 dark:text-green-400">Sign up to save your project and keep building.</p>
            </div>
          </div>
        </div>
      )}

      {/* Completion: authenticated */}
      {!isAgentRunning && messages.length > 0 && !isPublic && (
        <div className="border-t dark:border-gray-700 px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400 animate-fadeIn">
          Agent has finished. You can start a new task above.
        </div>
      )}
    </div>
  )
}
